class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    // Close button inside mega menu
    const closeBtn = this.querySelector('.mega-menu__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    }

    // Header close button (replaces CART link when menu is open)
    this.headerCloseBtn = document.getElementById('header-menu-close');
    if (this.headerCloseBtn) {
      this.headerCloseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        HeaderMenu.closeAllMenus();
      });
    }

    // Close other menus when this one is clicked
    this.mainDetailsToggle.querySelector('summary').addEventListener('click', (e) => {
      const isAnotherMenuOpen = this.isOtherMenuOpen();
      if (isAnotherMenuOpen) {
        // CRITICAL: Prevent default behavior to control the open/close timing ourselves
        e.preventDefault();

        // Switching between menus - use seamless transition
        this.seamlessMenuSwitch();
      }
    });
  }

  isOtherMenuOpen() {
    return Array.from(document.querySelectorAll('header-menu')).some(
      (menu) => menu !== this && menu.mainDetailsToggle.hasAttribute('open')
    );
  }

  seamlessMenuSwitch() {
    // Find the currently open menu
    const openMenu = Array.from(document.querySelectorAll('header-menu')).find(
      (menu) => menu !== this && menu.mainDetailsToggle.hasAttribute('open')
    );

    if (!openMenu) {
      // No menu is open, just open this one normally
      this.mainDetailsToggle.setAttribute('open', '');
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
      return;
    }

    const oldContent = openMenu.mainDetailsToggle.querySelector('.mega-menu__content');
    const newContent = this.mainDetailsToggle.querySelector('.mega-menu__content');

    // FORCE both menus to be fully visible during the entire switch
    // Must include display: block to override <details> native hiding behavior
    const forceVisible = 'display: block !important; opacity: 1 !important; visibility: visible !important; transition: none !important; transform: translateY(0) !important;';
    oldContent.style.cssText = forceVisible;
    newContent.style.cssText = forceVisible;

    // Force browser to apply these styles NOW
    oldContent.offsetHeight;
    newContent.offsetHeight;

    // Now do the swap - both are forced visible so no flash possible
    this.mainDetailsToggle.setAttribute('open', '');
    openMenu.mainDetailsToggle.removeAttribute('open');

    // Keep inline styles until after paint, then clean up
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        oldContent.style.cssText = '';
        newContent.style.cssText = '';
      });
    });
  }

  closeOtherMenus() {
    // Simple close for non-switching scenarios (like clicking outside)
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this && menu.mainDetailsToggle.hasAttribute('open')) {
        menu.close();
      }
    });
  }

  // Static method to close all mega menus
  static closeAllMenus() {
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu.mainDetailsToggle.hasAttribute('open')) {
        menu.close();
      }
    });
  }

  // Static method to check if any mega menu is open
  static isAnyMenuOpen() {
    return Array.from(document.querySelectorAll('header-menu')).some(
      (menu) => menu.mainDetailsToggle.hasAttribute('open')
    );
  }

  // Update body class based on menu state
  static updateBodyClass() {
    if (HeaderMenu.isAnyMenuOpen()) {
      document.body.classList.add('menu-drawer-open');
    } else {
      document.body.classList.remove('menu-drawer-open');
    }
    // Also update backdrop visibility
    HeaderMenu.updateBackdrop();
  }

  // Track the hide timeout
  static backdropHideTimeout = null;

  // Update backdrop visibility with 1s delay on hide
  static updateBackdrop() {
    const backdrop = document.getElementById('desk-menu-backdrop');
    if (!backdrop) return;

    if (HeaderMenu.isAnyMenuOpen()) {
      // Cancel any pending hide
      if (HeaderMenu.backdropHideTimeout) {
        clearTimeout(HeaderMenu.backdropHideTimeout);
        HeaderMenu.backdropHideTimeout = null;
      }
      // Show immediately
      backdrop.style.display = 'block';
    } else {
      // Hide with 0.25s delay
      if (!HeaderMenu.backdropHideTimeout) {
        HeaderMenu.backdropHideTimeout = setTimeout(() => {
          backdrop.style.display = 'none';
          HeaderMenu.backdropHideTimeout = null;
        }, 250);
      }
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    // Update body class for CART/CLOSE swap in header
    HeaderMenu.updateBodyClass();

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  close() {
    super.close();
    // Update body class when menu closes
    HeaderMenu.updateBodyClass();
  }
}

customElements.define('header-menu', HeaderMenu);
